// Inicialização do i18next (ICU via i18next-icu, fallback pt-BR) para o front-end GIYUS.
import i18next, { type i18n as I18nInstance, type InitOptions } from 'i18next';
import ICU from 'i18next-icu';
import { initReactI18next } from 'react-i18next';

import {
  DEFAULT_LOCALE,
  DEFAULT_NAMESPACE,
  type Locale,
  SUPPORTED_LOCALES,
  namespaces,
  resources,
} from './resources';

export {
  DEFAULT_LOCALE,
  DEFAULT_NAMESPACE,
  SUPPORTED_LOCALES,
  namespaces,
  resources,
} from './resources';
export type { Locale, Namespace, TranslationResources } from './resources';
export type { NamespaceKey, TranslationKey } from './types';

/** Nome do cabeçalho enviado em toda requisição com o idioma da UI (Parte 13.1). */
export const ACCEPT_LANGUAGE_HEADER = 'Accept-Language';

/** Chave de localStorage com a preferência de idioma do navegador (só conveniência local). */
export const LOCALE_STORAGE_KEY = 'giyus.locale';

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

const LANGUAGE_ALIASES: Readonly<Record<string, Locale>> = {
  pt: 'pt-BR',
  'pt-br': 'pt-BR',
  'pt-pt': 'pt-BR',
  en: 'en-US',
  'en-us': 'en-US',
  'en-gb': 'en-US',
};

/**
 * Resolve o primeiro idioma suportado a partir de candidatos (preferência salva, `navigator.languages`…).
 * Sem candidato válido devolve `DEFAULT_LOCALE` (pt-BR).
 */
export function resolveInitialLocale(candidates: readonly (string | null | undefined)[]): Locale {
  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }
    if (isLocale(candidate)) {
      return candidate;
    }
    const alias = LANGUAGE_ALIASES[candidate.toLowerCase()];
    if (alias) {
      return alias;
    }
    const base = candidate.split('-')[0]?.toLowerCase();
    const baseAlias = base ? LANGUAGE_ALIASES[base] : undefined;
    if (baseAlias) {
      return baseAlias;
    }
  }
  return DEFAULT_LOCALE;
}

export interface CreateI18nOptions {
  /** Idioma inicial; padrão `DEFAULT_LOCALE`. */
  readonly lng?: Locale;
  /** Registra `initReactI18next` (padrão `true`; use `false` em testes sem React). */
  readonly withReact?: boolean;
  /** Instância a inicializar; padrão `i18next.createInstance()`. */
  readonly instance?: I18nInstance;
}

/** Opções de inicialização comuns (sem plugins). */
export function buildInitOptions(lng: Locale = DEFAULT_LOCALE): InitOptions {
  return {
    resources,
    lng,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    ns: [...namespaces],
    defaultNS: DEFAULT_NAMESPACE,
    fallbackNS: DEFAULT_NAMESPACE,
    load: 'currentOnly',
    returnNull: false,
    returnEmptyString: false,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  };
}

/** Cria e inicializa uma instância do i18next com ICU e os recursos empacotados. */
export async function createI18n(options: CreateI18nOptions = {}): Promise<I18nInstance> {
  const instance = options.instance ?? i18next.createInstance();
  instance.use(ICU);
  if (options.withReact !== false) {
    instance.use(initReactI18next);
  }
  await instance.init(buildInitOptions(options.lng ?? DEFAULT_LOCALE));
  return instance;
}
